import { useEffect, useState } from "react";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, CircleNotch, CheckCircle, Warning, FileText } from "@phosphor-icons/react";

const FIELDS = [
  { k: "school_name", label: "School name" },
  { k: "bank_name", label: "School's bank", bank: true },
  { k: "account_number", label: "School's account number", mono: true },
  { k: "child_name", label: "Child's full name" },
  { k: "class_grade", label: "Class or grade" },
  { k: "amount_owed", label: "Amount owed (₦)", number: true },
  { k: "invoice_reference", label: "Invoice reference" },
];

const err = (e) => formatErr(e?.response?.data?.detail) || e.message;

function matchBank(banks, name) {
  const n = (name || "").toLowerCase().replace(/\b(plc|bank|limited|ltd)\b/g, "").trim();
  if (!n) return "";
  return banks.find((b) => b.name.toLowerCase().includes(n) || n.includes(b.name.toLowerCase().replace(/\b(plc|bank|limited|ltd)\b/g, "").trim()))?.code || "";
}

/** Non-partner school: upload invoice → AI reads it → parent edits & ticks each field → bank confirms account. */
export default function InvoiceLoanPath({ onConfirmed, onReset }) {
  const [banks, setBanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [inv, setInv] = useState(null);
  const [vals, setVals] = useState({});
  const [ticks, setTicks] = useState({});
  const [payee, setPayee] = useState(null);

  useEffect(() => { api.get("/banks").then((r) => setBanks(r.data)).catch(() => {}); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("The invoice must be smaller than 10 MB");
    setBusy(true); setPayee(null); setTicks({}); onReset?.();
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/loans/invoice/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const x = data.extracted || {};
      setInv(data);
      setVals({ ...x, amount_owed: x.amount_owed ?? "", bank_code: matchBank(banks, x.bank_name) });
      toast.success("Invoice read. Please check every detail below.");
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const set = (k, v) => { setVals((s) => ({ ...s, [k]: v })); setTicks((t) => ({ ...t, [k]: false })); setPayee(null); onReset?.(); };
  const allTicked = FIELDS.every((f) => ticks[f.k]);

  const confirm = async () => {
    setBusy(true);
    try {
      const bank = banks.find((b) => b.code === vals.bank_code);
      const { data } = await api.post(`/loans/invoice/${inv.invoice_id}/confirm`, {
        school_name: vals.school_name, school_email: vals.school_email || null,
        bank_code: vals.bank_code, bank_name: bank?.name || vals.bank_name,
        account_number: vals.account_number, child_name: vals.child_name, class_grade: vals.class_grade,
        amount_owed: Number(vals.amount_owed), invoice_reference: vals.invoice_reference,
        confirmed_fields: Object.keys(ticks).filter((k) => ticks[k]),
      });
      setPayee(data.payee);
      onConfirmed({ invoice_id: inv.invoice_id, payee: data.payee });
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  return (
    <div className="sm:col-span-2 p-4 rounded-2xl bg-[#F0EFEA]/60 border border-[#E5E3DB] space-y-4" data-testid="invoice-loan-path">
      <div>
        <div className="text-sm font-semibold text-[#0A3622] flex items-center gap-2"><FileText size={16} weight="fill" /> Upload your child's school fee invoice</div>
        <p className="text-xs text-[#4F5D4F] mt-1">PDF or photo, up to 10 MB. It must show the school's name, bank and account number, your child's full name, class and the amount owed. The loan is paid straight to the school's account on the invoice, never to you.</p>
      </div>
      <label className="btn-outline !py-2 text-sm cursor-pointer inline-flex" data-testid="invoice-upload-label">
        {busy && !inv ? <CircleNotch className="animate-spin" size={16} /> : <UploadSimple size={16} weight="bold" />}
        {inv ? "Upload a different invoice" : "Choose invoice file"}
        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])} data-testid="invoice-file-input" />
      </label>
      {inv && <div className="text-xs text-[#4F5D4F]" data-testid="invoice-filename">{inv.filename}</div>}

      {inv && (
        <div className="space-y-3" data-testid="invoice-review">
          <div className="text-xs text-[#4F5D4F]">Check each detail against the invoice, correct anything that's wrong, then tick it.</div>
          {FIELDS.map((f) => (
            <div key={f.k} className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{f.label}</span>
                {f.bank ? (
                  <select className="input mt-1" value={vals.bank_code || ""} onChange={(e) => { set("bank_code", e.target.value); setTicks((t) => ({ ...t, bank_name: false })); setVals((s) => ({ ...s, bank_name: banks.find((b) => b.code === e.target.value)?.name || s.bank_name })); }} data-testid="invoice-field-bank_name">
                    <option value="">{vals.bank_name ? `Pick the bank for "${vals.bank_name}"` : "Choose the bank"}</option>
                    {banks.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
                  </select>
                ) : (
                  <input className={`input mt-1 ${f.mono ? "font-mono" : ""}`} type={f.number ? "number" : "text"} value={vals[f.k] ?? ""}
                    maxLength={f.k === "account_number" ? 10 : 160}
                    onChange={(e) => set(f.k, f.k === "account_number" ? e.target.value.replace(/\D/g, "") : e.target.value)} data-testid={`invoice-field-${f.k}`} />
                )}
              </label>
              <label className="flex items-center gap-1.5 text-xs text-[#0A3622] font-semibold pb-3 cursor-pointer">
                <input type="checkbox" checked={!!ticks[f.k]} disabled={f.bank ? !vals.bank_code : !String(vals[f.k] ?? "").trim()}
                  onChange={(e) => setTicks((t) => ({ ...t, [f.k]: e.target.checked }))} data-testid={`invoice-tick-${f.k}`} />
                Correct
              </label>
            </div>
          ))}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">School email (optional, for the payment notice)</span>
            <input type="email" className="input mt-1" value={vals.school_email || ""} onChange={(e) => set("school_email", e.target.value)} data-testid="invoice-field-school_email" />
          </label>
          {!payee ? (
            <button type="button" onClick={confirm} disabled={busy || !allTicked} className="btn-primary !py-2 disabled:opacity-50" data-testid="invoice-confirm">
              {busy ? <CircleNotch className="animate-spin" size={16} /> : "Confirm details and check school account"}
            </button>
          ) : (
            <div className="rounded-xl bg-white border border-[#E5E3DB] p-3 text-sm space-y-1" data-testid="invoice-payee-confirmed">
              <div className="flex items-center gap-2 text-[#2D7A5D] font-semibold"><CheckCircle size={16} weight="fill" /> Bank confirmed the account</div>
              <div>Account name: <strong data-testid="invoice-account-name">{payee.account_name}</strong></div>
              <div className="text-xs text-[#4F5D4F]">{payee.bank_name} · {payee.account_number} · up to {fmtNGN(payee.amount_owed)}</div>
              {payee.name_match < 50 && (
                <div className="flex items-start gap-2 text-xs text-[#B45309]" data-testid="invoice-name-warning">
                  <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                  This account name doesn't look like "{payee.school_name}". A credit officer will check it with the school before any payout.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
